import { abstandUndRichtung, entfernungText, naechstgelegen } from "@dj/domain/lage";
import type { Zeile } from "../plattform/lesemodelle";
import { personAus, positionAus } from "./daten";

/**
 * Eingehender Notruf der Leitung (oberflaeche.md §5, design/README §9.10): Jagdherr und Jagdleiter sehen jeden offenen
 * Notruf der Drückjagd, den noch niemand quittiert hat (`Notrufstatus` „abgesetzt“), bis er quittiert ist — den ältesten
 * zuerst; den eigenen Notruf nicht (die Melderin hat den Anruf im Vordergrund). Ein auf diesem Gerät gerade quittierter
 * Notruf gilt als quittiert, bevor das Lesemodell es zeigt.
 */
export function eingehenderNotruf(notrufe: readonly Zeile[], ich: string | null, quittiert: ReadonlySet<string>): Zeile | null {
  const zeit = (n: Zeile) => (n["Zeitpunkt"] instanceof Date ? n["Zeitpunkt"].getTime() : 0);
  return [...notrufe]
    .filter((n) => n["Notrufstatus"] === "abgesetzt" && !quittiert.has(n._id))
    .filter((n) => !ich || personAus(n["Melder"])?.Kennung !== ich)
    .sort((a, b) => zeit(a) - zeit(b) || a._id.localeCompare(b._id))[0] ?? null;
}

export interface Rettungspunkt {
  Kennung: string;
  Lage: string | null;
  Breite: number;
  Länge: number;
}

export function rettungspunkteAus(zeilen: readonly Zeile[]): Rettungspunkt[] {
  return zeilen.map((r) => ({ Kennung: String(r["Kennung"]), Lage: (r["Lage"] as string | null) ?? null, Breite: Number(r["Breite"]), Länge: Number(r["Länge"]) }))
    .filter((r) => Number.isFinite(r.Breite) && Number.isFinite(r.Länge));
}

/** Rettungspunkt zum Notruf: der nächstgelegene zur Position des Melders, mit Entfernung („UE-201 · 230 m“). */
export function rettungspunktZum(notruf: Zeile, punkte: readonly Rettungspunkt[]): { punkt: Rettungspunkt; text: string } | null {
  const position = positionAus(notruf["Position"]);
  const punkt = position ? naechstgelegen(position, punkte) : null;
  if (!position || !punkt) return null;
  return { punkt, text: `${punkt.Kennung} · ${entfernungText(abstandUndRichtung(position, punkt).meter)}` };
}

/** Entfernung des Melders vom eigenen Gerät mit Richtung („1,2 km NO“); ohne eine der Positionen `null`. */
export function entfernungVomGeraet(notruf: Zeile, geraet: { Breite: number; Länge: number } | null): string | null {
  const position = positionAus(notruf["Position"]);
  if (!position || !geraet) return null;
  const { meter, richtung } = abstandUndRichtung(geraet, position);
  return `${entfernungText(meter)} ${richtung}`;
}

export const NOTFALLSYMBOL: Record<string, string> = {
  "Person verletzt": "person-verletzt", "Hund verletzt": "hund-verletzt", "sonstiger Notfall": "sonstiger-notfall",
};
